import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, type DeckListItem, type Insight, type Stats, type TalkStats, api, issuesOf } from "../../lib/api";
import { ConfirmButton } from "./fields";
import "./studio.css";
import { APP_NAME } from "../../lib/brand";
import { SignOutButton } from "./SignOut";

/* ---------- formatting ---------- */

const pct = (x: number | null) => (x === null ? "–" : `${Math.round(x * 100)}%`);
const compact = (n: number) =>
  n >= 10_000 ? `${(n / 1000).toFixed(0)}K` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);

function clock(ms: number): string {
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

function hours(ms: number): string {
  const min = Math.round(ms / 60_000);
  return min < 60 ? `${min} min` : `${(min / 60).toFixed(1)} h`;
}

function ago(iso: string, now = Date.now()): string {
  const days = Math.floor((now - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 14) return `${days} days ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

const day = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

/* ---------- pieces ---------- */

function Tile({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="tile">
      <span className="tile-label">{label}</span>
      <span className="tile-value">{value}</span>
      {note && <span className="tile-note">{note}</span>}
    </div>
  );
}

/** People reached per week, oldest to this week. One series, so no legend: the title names it. */
function WeeksChart({ weeks }: { weeks: Stats["weeks"] }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...weeks.map((w) => w.people));
  const top = max <= 4 ? max : Math.ceil(max / 5) * 5;
  const shown = hover ?? weeks.length - 1;
  const w = weeks[shown]!;
  return (
    <figure className="card chart">
      <figcaption className="chart-head">
        <span className="card-title">People reached per week</span>
        <span className="chart-readout" aria-live="polite">
          {hover === null ? "This week" : `Week of ${day(w.start)}`}: <strong>{w.people}</strong>{" "}
          {w.people === 1 ? "person" : "people"} · {w.sessions} {w.sessions === 1 ? "session" : "sessions"}
        </span>
      </figcaption>
      <div className="chart-plot">
        <div className="chart-axis" aria-hidden="true">
          <span>{top}</span>
          <span>0</span>
        </div>
        <div className="chart-bars" onMouseLeave={() => setHover(null)}>
          {weeks.map((x, i) => (
            <button
              key={x.start}
              type="button"
              className="chart-col"
              data-current={i === weeks.length - 1}
              data-active={i === shown}
              aria-label={`Week of ${day(x.start)}: ${x.people} people, ${x.sessions} sessions`}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
            >
              <span className="chart-bar" style={{ height: `${(x.people / top) * 100}%` }} />
            </button>
          ))}
        </div>
      </div>
      <div className="chart-x" aria-hidden="true">
        <span>{day(weeks[0]!.start)}</span>
        <span>This week</span>
      </div>
      <table className="sr-only">
        <caption>People reached per week</caption>
        <thead>
          <tr>
            <th>Week of</th>
            <th>Sessions</th>
            <th>People</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((x) => (
            <tr key={x.start}>
              <td>{x.start}</td>
              <td>{x.sessions}</td>
              <td>{x.people}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

const TONE_LABEL: Record<Insight["tone"], string> = { attention: "Act on", good: "Going well", neutral: "Next" };

function ToneIcon({ tone }: { tone: Insight["tone"] }) {
  if (tone === "good")
    return (
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
        <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  if (tone === "attention")
    return (
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
        <path d="M8 3.5v5.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="8" cy="12" r="1" fill="currentColor" />
      </svg>
    );
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <path d="M4 8h8M9 5l3 3-3 3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function Insights({ items }: { items: Insight[] }) {
  if (!items.length) return null;
  return (
    <section className="card insights" aria-label="Insights">
      <span className="card-title">What to look at</span>
      <ul>
        {items.map((i) => {
          const body = (
            <>
              <span className="insight-icon" data-tone={i.tone}>
                <ToneIcon tone={i.tone} />
              </span>
              <span className="insight-text">
                <span className="insight-kind">{TONE_LABEL[i.tone]}</span>
                <span className="insight-title">{i.title}</span>
                <span className="insight-detail">{i.detail}</span>
              </span>
            </>
          );
          return (
            <li key={`${i.kind}:${i.deckId ?? ""}`}>
              {i.deckId ? (
                <Link className="insight" to={`/studio/${i.deckId}`}>
                  {body}
                </Link>
              ) : (
                <div className="insight">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * How much of the room answered each question, in slide order: one column per question on a 0–100% scale (the top
 * line is everyone who joined). Hover or focus a column for the question and its count.
 */
function Participation({ s }: { s: Stats["recent"][number] }) {
  const [active, setActive] = useState<number | null>(null);
  if (!s.questions.length || !s.people) return null;
  const share = (n: number) => Math.min(1, n / s.people);
  const q = active === null ? null : s.questions[active]!;
  return (
    <span className="spark" onMouseLeave={() => setActive(null)}>
      <span className="spark-plot" role="group" aria-label={`Answered per question, session ${s.code}`}>
        {s.questions.map((x, i) => (
          <button
            key={x.blockId}
            type="button"
            className="spark-col"
            data-active={i === active}
            aria-label={`Question ${i + 1}, ${x.type}: ${x.question}. ${x.answered} of ${s.people} answered`}
            onMouseEnter={() => setActive(i)}
            onFocus={() => setActive(i)}
            onBlur={() => setActive(null)}
          >
            <span className="spark-bar" style={{ height: `${share(x.answered) * 100}%` }} />
          </button>
        ))}
      </span>
      {q && (
        <span className="spark-tip" role="status">
          <span className="spark-tip-q">
            {active! + 1}. {q.question}
          </span>
          <strong>
            {q.answered} of {s.people}
          </strong>{" "}
          answered · {Math.round(share(q.answered) * 100)}%
        </span>
      )}
    </span>
  );
}

function Recent({ sessions, onEnd }: { sessions: Stats["recent"]; onEnd: (code: string) => void }) {
  if (!sessions.length) return null;
  return (
    <section className="card" aria-label="Recent sessions">
      <span className="card-title">Recent sessions</span>
      <div className="table-wrap">
        <table className="stats-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Talk</th>
              <th className="num">People</th>
              <th className="num">Answers</th>
              <th className="num">Answered</th>
              <th className="num">Length</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.code}>
                <td>
                  {ago(s.createdAt)} <span className="code-tag">{s.code}</span>
                  {s.status === "LIVE" && <span className="deck-badge">LIVE</span>}
                </td>
                <td>
                  <Link to={`/studio/${s.deckId}`}>{s.deckTitle}</Link>
                </td>
                <td className="num">{s.people}</td>
                <td className="num">{s.responses}</td>
                <td className="num">
                  <span className="answered-cell">
                    <Participation s={s} />
                    <span className="answered-pct">{pct(s.responseRate)}</span>
                  </span>
                </td>
                <td className="num">{s.talkMs === null ? "–" : clock(s.talkMs)}</td>
                <td className="row-actions">
                  {s.status === "LIVE" ? (
                    <ConfirmButton
                      className="s-btn s-btn-small"
                      label="End"
                      confirmLabel="End for everyone?"
                      onConfirm={() => onEnd(s.code)}
                    />
                  ) : (
                    <a className="link-btn" href={`/api/sessions/${s.code}/responses.csv`} download>
                      CSV
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** One line of what the talk has done: given, reach, answers, quiz, rehearsal against its slot. */
function talkLine(d: DeckListItem, t: TalkStats | undefined): string[] {
  if (!t) return [];
  const bits: string[] = [];
  if (t.sessions)
    bits.push(
      `given ${t.sessions}× · ${t.people} ${t.people === 1 ? "person" : "people"}`,
      `${pct(t.responseRate)} answered`,
    );
  if (t.quizAccuracy !== null) bits.push(`quiz ${pct(t.quizAccuracy)} right`);
  const run = t.lastLive ?? t.lastRehearsal;
  if (run) bits.push(`${t.lastLive ? "last talk" : "rehearsed"} ${clock(run.totalMs)} of ${d.minutes}:00`);
  else if (!t.sessions) bits.push("not rehearsed yet");
  return bits;
}

/* ---------- page ---------- */

export function StudioListPage() {
  const [decks, setDecks] = useState<DeckListItem[] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [archived, setArchived] = useState<DeckListItem[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const load = () =>
    Promise.all([
      api.decks().then(setDecks),
      api.archivedDecks().then(setArchived),
      api.stats().then(setStats, () => setStats(null)), // the list still works if stats fail
    ]).catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 401) navigate("/login?next=/studio", { replace: true });
      else setError(String(e));
    });

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const d = await api.createDeck(title.trim() || "Untitled talk");
      navigate(`/studio/${d.id}`);
    } catch (err) {
      setError(String(err));
    }
  };

  const duplicate = async (id: string) => {
    try {
      const d = await api.duplicateDeck(id);
      navigate(`/studio/${d.id}`);
    } catch (err) {
      setError(String(err));
    }
  };

  const endSession = (code: string) => api.endSession(code).then(load, (err: unknown) => setError(String(err)));

  const onImport = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError(null);
    try {
      const d = await api.importDeck(JSON.parse(await f.text()));
      navigate(`/studio/${d.id}`);
    } catch (err) {
      const issues = issuesOf(err);
      setError(
        issues.length ? issues.map((i) => `${i.path}: ${i.message}`).join("\n") : `Import failed: ${String(err)}`,
      );
    }
  };

  const t = stats?.totals;
  return (
    <div className="studio">
      <header className="studio-bar">
        <div className="studio-crumbs">
          <span className="studio-wordmark">{APP_NAME}</span>
          <span className="studio-sep">/</span>
          <span className="studio-title">Studio</span>
        </div>
        <div className="studio-actions">
          <SignOutButton />
          <button type="button" className="s-btn" onClick={() => file.current?.click()}>
            Import JSON
          </button>
          <input ref={file} type="file" accept="application/json,.json" hidden onChange={onImport} />
        </div>
      </header>
      <div className="decks-page dashboard">
        <div className="decks-head">
          <h1 className="decks-h">Your talks</h1>
          <form className="new-deck" onSubmit={create}>
            <input
              aria-label="New talk title"
              placeholder="New talk title"
              value={title}
              maxLength={80}
              onChange={(e) => setTitle(e.target.value)}
            />
            <button type="submit" className="s-btn s-btn-ink">
              New talk
            </button>
          </form>
        </div>
        {error && (
          <p className="banner banner-yellow" role="alert" style={{ whiteSpace: "pre-wrap" }}>
            {error}
          </p>
        )}

        {t && (
          <section className="tiles" aria-label="Totals">
            <Tile
              label="People reached"
              value={compact(t.people)}
              note={`${t.sessions} ${t.sessions === 1 ? "session" : "sessions"}`}
            />
            <Tile label="Answers" value={compact(t.responses)} note="votes and replies on phones" />
            <Tile label="Answered per question" value={pct(t.responseRate)} note="average share of the room" />
            <Tile label="Quiz answers right" value={pct(t.quizAccuracy)} />
            <Tile
              label="Rehearsal time"
              value={hours(t.rehearsalMs)}
              note={`${t.rehearsals} ${t.rehearsals === 1 ? "run" : "runs"} over a minute`}
            />
          </section>
        )}
        {stats && t && t.sessions === 0 && (
          <p className="empty">
            No live sessions yet. Present a talk and the room’s answers, quiz scores and timing show up here.
          </p>
        )}
        {stats && (t?.sessions ?? 0) > 0 && (
          <div className="dash-grid">
            <WeeksChart weeks={stats.weeks} />
            <Insights items={stats.insights} />
          </div>
        )}
        {stats && (t?.sessions ?? 0) === 0 && <Insights items={stats.insights} />}
        {stats && <Recent sessions={stats.recent} onEnd={endSession} />}

        {decks?.length === 0 && (
          <p className="empty">No talks yet. Name one above, then paste your notes and generate.</p>
        )}
        {decks && decks.length > 0 && <span className="card-title list-title">All talks</span>}
        <ul className="deck-rows">
          {decks?.map((d) => {
            const ts = stats?.talks[d.id];
            const line = talkLine(d, ts);
            return (
              <li key={d.id} className="deck-row2">
                <Link className="deck-name" to={`/studio/${d.id}`}>
                  <span className="deck-name-title">
                    {d.title}
                    {d.liveCode && (
                      <span className="deck-badge" title="A live session is running; Present rejoins it">
                        LIVE · {d.liveCode}
                      </span>
                    )}
                    {(ts?.toFix ?? 0) > 0 && (
                      <span className="fix-badge" title="Placeholders, unread link titles or missing images">
                        {ts!.toFix} to fix
                      </span>
                    )}
                  </span>
                  <span className="deck-name-meta">
                    {[
                      d.presenter,
                      `${d.sections} ${d.sections === 1 ? "section" : "sections"}`,
                      `${d.slides} slides`,
                      `${d.minutes} min`,
                      `v${d.version}`,
                      `edited ${ago(d.updatedAt)}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                      .toUpperCase()}
                  </span>
                  {line.length > 0 && <span className="deck-stats">{line.join(" · ")}</span>}
                </Link>
                <Link className="s-btn" to={`/studio/${d.id}`}>
                  Edit
                </Link>
                <a className="s-btn" href={`/present/${d.id}?rehearse=1`} target="_blank" rel="noopener">
                  Rehearse
                </a>
                <a className="s-btn s-btn-yellow" href={`/present/${d.id}`} target="_blank" rel="noopener">
                  {d.liveCode ? "Rejoin live" : "Present"}
                </a>
                {d.liveCode && (
                  <ConfirmButton
                    className="s-btn"
                    label="End live"
                    confirmLabel="End for everyone?"
                    onConfirm={() => endSession(d.liveCode!)}
                  />
                )}
                <button type="button" className="s-btn" onClick={() => duplicate(d.id)}>
                  Duplicate
                </button>
                <ConfirmButton
                  className="s-btn"
                  label="Archive"
                  confirmLabel="Archive it?"
                  onConfirm={() => api.archiveDeck(d.id).then(load)}
                />
              </li>
            );
          })}
        </ul>
        {archived.length > 0 && (
          <section className="archived" aria-label="Archived talks">
            <button
              type="button"
              className="link-btn"
              aria-expanded={showArchived}
              onClick={() => setShowArchived(!showArchived)}
            >
              {showArchived ? "Hide" : "Show"} archived talks ({archived.length})
            </button>
            {showArchived && (
              <>
                <p className="help">
                  Archived talks keep their versions, sessions and answers; they still count in the totals above.
                </p>
                <ul className="deck-rows">
                  {archived.map((d) => (
                    <li key={d.id} className="deck-row2 deck-row-archived">
                      <Link className="deck-name" to={`/studio/${d.id}`}>
                        <span className="deck-name-title">{d.title}</span>
                        <span className="deck-name-meta">
                          {`${d.sections} sections · ${d.minutes} min · v${d.version} · archived ${ago(d.archivedAt!)}`.toUpperCase()}
                        </span>
                      </Link>
                      <button
                        type="button"
                        className="s-btn"
                        onClick={() => api.unarchiveDeck(d.id).then(load, (e: unknown) => setError(String(e)))}
                      >
                        Restore
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
